import { graphql, useStaticQuery } from 'gatsby'
import React from 'react'
import LazyLoad from 'react-lazyload'
import { twMerge } from 'tailwind-merge'

const placeholderImage = require('../../../static/images/utils/video-thumbnail-placeholder.png')

interface Props {
	/**
	 * This must match a Video content type from Contentful
	 */
	title: string
	/**
	 * If true, will set the video to automatically play
	 *
	 * Set to `false` by default
	 */
	autoPlay?: boolean
	/**
	 * If set to true, there will be no thumbnail overlay
	 *
	 * Set to `false` by default
	 */
	noOverlay?: boolean
	/**
	 * Used to set the video to a particualr timestamp
	 *
	 * @example timeStamp="3m23s"
	 */
	timeStamp?: string
}

const OverlayVideo = ({ title, autoPlay, noOverlay, timeStamp }: Props) => {
	// Set the query data to a variable
	const data = useStaticQuery<VideoQuery>(query).allContentfulVideo.edges.filter((resource) => resource.node.title === title)[0].node

	// Declaring the state
	const [overlayVisible, setOverlayVisible] = React.useState(autoPlay || noOverlay ? false : true)
	const [playButtonVisible, setPlayButtonVisible] = React.useState(autoPlay || noOverlay ? false : true)
	const [videoSrc, setVideoSrc] = React.useState(autoPlay ? data.url + determineAutoplayFormat(data.url) : data.url)

	function determineAutoplayFormat(url: string) {
		if (url.includes('youtube')) return '?autoplay=1&rel=0'
		if (url.includes('vimeo')) return ''
		else return '?autoplay=1&rel=0'
	}

	return (
		<LazyLoad>
			<div className={`relative border-4 border-slate-800`}>
				<iframe data-cy="overlay-video-iframe" loading="lazy" className="aspect-video w-full" allow="autoplay" title={data.title} src={videoSrc} allowFullScreen />
				<div
					className={twMerge('absolute top-0 h-full w-full', overlayVisible ? 'block' : 'hidden')}
					style={{
						background: `url(${data.thumbnail ? data.thumbnail.file.url : placeholderImage})`,
						backgroundPosition: 'center',
						backgroundSize: 'contain'
					}}
				/>
				<button
					className={`absolute left-1/2 top-1/2 cursor-pointer opacity-75 ${playButtonVisible ? 'block' : 'hidden'} `}
					style={{ marginLeft: -48, marginTop: -50 }}
					onClick={() => {
						setOverlayVisible(false)
						setPlayButtonVisible(false)
						setVideoSrc(`${videoSrc}${determineAutoplayFormat(data.url)}${timeStamp ? `t=${timeStamp}` : ''}`)
					}}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						className="h-24 w-24 text-ar-100"
						style={data.iconColor ? { color: data.iconColor } : {}}
						viewBox="0 0 20 20"
						fill="currentColor"
					>
						<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
					</svg>
				</button>
			</div>
		</LazyLoad>
	)
}

interface VideoQueryNode {
	node: {
		title: string
		url: string
		iconColor: string
		thumbnail: {
			file: {
				url: string
			}
		}
	}
}

interface VideoQuery {
	allContentfulVideo: {
		edges: VideoQueryNode[]
	}
}

const query = graphql`
	query {
		allContentfulVideo {
			edges {
				node {
					title
					url
					iconColor
					thumbnail {
						file {
							url
						}
					}
				}
			}
		}
	}
`

export default OverlayVideo
